import React, { useEffect, useState } from 'react';
import { ccmApi } from '../lib/api';
import { optionLabel, optionName, modelOptionLabel, copyFor } from '../lib/note-expander-v2';
import {
  PRESETS, presetLabel, presetRange, rangeProblem, crossesExpansion, expansionWarning, shownProfile, styleChip,
  learnedLine, sectionOrder, habitsOf, systemLabel, countWords, historyLine, approvedDate,
} from '../lib/note-expander-v2-style';

// Note Expander v2, "Your style" (PR 8a-UI; DESIGN §8.1, mockup screen 6, rulings #196/#197, O196).
// Auto customize learns a physician's style from their own signed notes; nothing it learns is used
// until they approve it. Everything here comes from the server: GET /v2/me/style (what was learned,
// what is in use as `approved.hasLearned`, the range presets in the server's date maths), the live
// count (GET /v2/me/style/count), and the models (GET /v2/models; nothing here names one, the
// W-GATE-ALL sibling pin).
//   1 · Which of your notes   the presets, custom dates, the live count and the build's checks, and
//                             the warning when the range reaches notes signed after the expander began.
//   2 · Which model           Build, or Rebuild (the approved style stays in use until the new one is).
//   3 · What we learned       the draft under review, else the approved learned style: its settings,
//                             section order, A&P shape, phrases (Remove), habits (Remove; each with how
//                             many notes show it and one de-identified quote; Grounded runs only),
//                             examples; Approve, Edit,
//                             "Keep my current style" (a draft over an approved learned style),
//                             "Use standard style instead".
//   · Style history           (PR 8b-1) every version approved once, newest first, in words; Restore
//                             puts an earlier one back as a new version (POST /v2/me/style/revert).

const POLL_MS = 3000;

export function NoteExpanderV2Style({ style, models, copy, practiceDefaultModel, onChanged, onEditPrefs }) {
  const entries = (models && models.models) || [];
  const first = style.defaults ? style.defaults.firstExpansion : null;
  const [preset, setPreset] = useState('m6');
  const [range, setRange] = useState(() => presetRange(style, 'm6'));
  const [count, setCount] = useState({ state: 'idle', n: null });
  const [model, setModel] = useState('');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  const building = !!(style.draft && style.draft.state === 'building');
  const failed = !!(style.draft && style.draft.state === 'failed');
  const approvedLearned = !!(style.approved && style.approved.hasLearned);
  const shown = shownProfile(style);
  const chip = styleChip(style);

  // The live count: Elation's own, for the range as it stands (debounced; checks first).
  useEffect(() => {
    if (!range || rangeProblem(range, style.today, null, style.minNotes)) { setCount({ state: 'idle', n: null }); return undefined; }
    let live = true;
    setCount({ state: 'loading', n: null });
    const t = setTimeout(() => {
      ccmApi.ne2StyleCount(range)
        .then((r) => { if (live) setCount({ state: 'ok', n: r.count }); })
        .catch((e) => {
          if (!live) return;
          const reason = e && e.body && e.body.reason;
          setCount({ state: reason === 'no_physician' ? 'unlinked' : 'error', n: null });
        });
    }, 400);
    return () => { live = false; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range && range.from, range && range.to]);

  // While a build runs, the page asks again every few seconds until it settles.
  useEffect(() => {
    if (!building) return undefined;
    const t = setTimeout(() => { onChanged().catch(() => {}); }, POLL_MS);
    return () => clearTimeout(t);
  }, [building, style, onChanged]);

  function choosePreset(key) {
    setPreset(key);
    if (key !== 'custom') setRange(presetRange(style, key));
  }
  function setDate(field, value) {
    setPreset('custom');
    setRange((r) => ({ ...(r || {}), [field]: value }));
  }

  // The action, then the page's refresh: a refresh that fails after the action succeeded never
  // reads as the action failing.
  async function act(name, call) {
    setBusy(name);
    setError('');
    try {
      await call();
    } catch (e) {
      const reason = e && e.body && e.body.reason;
      setError(copyFor(copy, 'style', reason) || 'That did not work. Try again.');
      setBusy('');
      return;
    }
    try {
      await onChanged();
    } catch (_) {
      setError('Done. The page could not refresh: reload it to see the change.');
    } finally {
      setBusy('');
    }
  }

  const unlinked = count.state === 'unlinked';
  const problem = rangeProblem(range, style.today, count.state === 'ok' ? count.n : null, style.minNotes);
  const canBuild = !busy && !building && !unlinked && !problem && count.state === 'ok';
  const modelName = (alias) => {
    const e = entries.find((m) => m.alias === alias);
    return e ? e.display : alias;
  };

  return (
    <div className="ne2-prefs ne2-style">
      <div className="ne2-style-head">
        <div>
          <h3>Your style</h3>
          <p className="ne2-pref-note">Learned from your own signed notes. Names, dates and numbers are removed.</p>
        </div>
        <span className={'ne2-style-chip' + (chip.tone ? ` is-${chip.tone}` : '')}>{chip.label}</span>
      </div>
      {error && <div className="ne2-field-error" role="alert">{error}</div>}

      <section className="ne2-pref">
        <div className="ne2-pref-head"><h3>1 · Which of your notes to learn from</h3></div>
        <div className="ne2-seg" role="radiogroup" aria-label="Which of your notes">
          {PRESETS.map((key) => (
            <button key={key} type="button" role="radio" aria-checked={preset === key}
                    className={'ne2-seg-btn' + (preset === key ? ' is-on' : '')}
                    onClick={() => choosePreset(key)}>{presetLabel(key, first)}</button>
          ))}
        </div>
        <div className="ne2-style-range">
          <label>Signed from{' '}
            <input type="date" value={(range && range.from) || ''} max={style.today}
                   onChange={(e) => setDate('from', e.target.value)} />
          </label>
          <label>to{' '}
            <input type="date" value={(range && range.to) || ''} max={style.today}
                   onChange={(e) => setDate('to', e.target.value)} />
          </label>
          <span className="ne2-count" aria-live="polite">{countWords(count)}</span>
        </div>
        {crossesExpansion(range, first) && <p className="ne2-notice ne2-style-warn">{expansionWarning(first)}</p>}
        {preset === 'all' && (
          <p className="ne2-pref-note">Up to {style.scanMax} of your signed notes are read, and a sample spread across months is used.</p>
        )}
        {unlinked && <div className="ne2-field-error" role="alert">{copyFor(copy, 'style', 'no_physician')}</div>}
        {!unlinked && problem && count.state !== 'loading' && <div className="ne2-field-error" role="alert">{problem}</div>}
      </section>

      <section className="ne2-pref">
        <div className="ne2-pref-head">
          <h3>2 · Which model reads them</h3>
          <p>Any model in the note expander's list. Whichever you pick, the same safety checks run on what it learns.</p>
        </div>
        <select className="ne2-select" value={model} aria-label="Model that reads your notes" onChange={(e) => setModel(e.target.value)}>
          <option value="">Practice default</option>
          {entries.map((m) => (
            <option key={m.alias} value={m.alias}>{modelOptionLabel(m, practiceDefaultModel)}</option>
          ))}
        </select>
        {models && models.marksNote && <p className="ne2-pref-note">{models.marksNote}</p>}
        <div className="ne2-style-actions">
          <button type="button" className="btn accent" disabled={!canBuild}
                  onClick={() => act('build', () => ccmApi.ne2StyleBuild({ ...range, model: model || practiceDefaultModel || null }))}>
            {building ? 'Building…' : (shown || approvedLearned ? 'Rebuild from these notes' : 'Build my style')}
          </button>
          {approvedLearned && <span className="ne2-pref-note">Your approved style stays in use until you approve the new one.</span>}
        </div>
        {building && <p className="ne2-pref-note" role="status">Learning from your notes. This takes a minute or two, and you can leave this page.</p>}
        {failed && <div className="ne2-field-error" role="alert">{copyFor(copy, 'style', style.draft.failure) || 'The build did not finish. Rebuild to try again.'}</div>}
      </section>

      {shown && (
        <section className="ne2-pref ne2-style-learned">
          <div className="ne2-pref-head">
            <h3>3 · What we learned</h3>
            <p>{learnedLine(shown.profile, modelName(shown.profile.model))}{shown.kind === 'draft' ? ' · not used until you approve it' : ''}</p>
          </div>
          <dl className="ne2-style-table">
            {Object.keys(shown.profile.options || {}).map((key) => (
              <React.Fragment key={key}>
                <dt>{optionName(key)}</dt>
                <dd>{optionLabel(key, shown.profile.options[key])}</dd>
              </React.Fragment>
            ))}
            {sectionOrder(shown.profile.structure).length > 0 && (
              <React.Fragment>
                <dt>Section order</dt>
                <dd>{sectionOrder(shown.profile.structure).join(' → ')}</dd>
              </React.Fragment>
            )}
            {shown.profile.structure && shown.profile.structure.apShape && (
              <React.Fragment>
                <dt>How you write the A&amp;P</dt>
                <dd>{shown.profile.structure.apShape}</dd>
              </React.Fragment>
            )}
          </dl>
          {shown.profile.phrases.length > 0 && (
            <div className="ne2-style-block">
              <h4>Your normal-exam phrases</h4>
              {shown.profile.phrases.map((ph) => (
                <div key={ph.phraseId} className="ne2-style-phrase">
                  <span className="ne2-style-label">{systemLabel(ph)}</span>
                  <span className="ne2-style-text">{ph.text}</span>
                  {ph.notes != null && <span className="ne2-pref-note">in {ph.notes} notes</span>}
                  <button type="button" className="btn sm ghost" disabled={!!busy}
                          onClick={() => act('remove', () => ccmApi.ne2StyleRemovePhrase(ph.phraseId, shown.kind))}>Remove</button>
                </div>
              ))}
            </div>
          )}
          {habitsOf(shown.profile).length > 0 && (
            <div className="ne2-style-block">
              <h4>Your writing habits</h4>
              <p className="ne2-pref-note">Habits seen in your own notes, about layout and wording. Notes you start yourself in Grounded mode use the ones you keep; automatic sweeps and Maximal never do. Remove any that are not you, and any that would add something to a note.</p>
              {habitsOf(shown.profile).map((h) => (
                <div key={h.id} className="ne2-style-habit">
                  <span className="ne2-style-text">{h.text}</span>
                  {h.notes != null && <span className="ne2-pref-note">in {h.notes} notes</span>}
                  <button type="button" className="btn sm ghost" disabled={!!busy}
                          onClick={() => act('remove', () => ccmApi.ne2StyleRemoveHabit(h.id, shown.kind))}>Remove</button>
                  {h.example && (
                    <details className="ne2-style-habit-example">
                      <summary>Example from your notes</summary>
                      <pre className="ne2-style-text">{h.example}</pre>
                    </details>
                  )}
                </div>
              ))}
            </div>
          )}
          {shown.profile.exemplars && ['hpi', 'exam', 'ap'].some((k) => shown.profile.exemplars[k]) && (
            <div className="ne2-style-block">
              <h4>Examples of your voice (de-identified)</h4>
              {[['hpi', 'HPI'], ['exam', 'Exam'], ['ap', 'Plan']].filter(([k]) => shown.profile.exemplars[k]).map(([k, label]) => (
                <div key={k} className="ne2-style-example">
                  <span className="ne2-style-label">{label}</span>
                  <pre className="ne2-style-text">{shown.profile.exemplars[k]}</pre>
                </div>
              ))}
            </div>
          )}
          <div className="ne2-save ne2-style-decide">
            {shown.kind === 'draft' && (
              <button type="button" className="btn accent" disabled={!!busy} onClick={() => act('approve', () => ccmApi.ne2StyleApprove())}>
                {busy === 'approve' ? 'Approving…' : 'Approve'}
              </button>
            )}
            <button type="button" className="btn sm" onClick={onEditPrefs}>Edit</button>
            {shown.kind === 'draft' && approvedLearned && (
              <button type="button" className="btn sm" disabled={!!busy} onClick={() => act('discard', () => ccmApi.ne2StyleDiscard())}>Keep my current style</button>
            )}
            <button type="button" className="btn sm ghost" disabled={!!busy} onClick={() => act('standard', () => ccmApi.ne2StyleStandard())}>
              Use standard style instead
            </button>
          </div>
        </section>
      )}

      {Array.isArray(style.history) && style.history.length > 0 && (
        <section className="ne2-pref">
          <div className="ne2-pref-head">
            <h3>{shown ? 4 : 3} · Style history</h3>
            <p>Every version you approved, or that replaced one. Restore puts an earlier one back as a new version.</p>
          </div>
          <div className="ne2-style-block">
            {style.history.map((h) => (
              <div key={h.id} className="ne2-style-history">
                <span className="ne2-style-label">v{h.version}{approvedDate(h.approvedAt) ? ` · ${approvedDate(h.approvedAt)}` : ''}</span>
                <span className="ne2-style-text">{historyLine(h)}</span>
                {h.status === 'approved'
                  ? <span className="ne2-style-chip is-ok">In use</span>
                  : (
                    <button type="button" className="btn sm ghost" disabled={!!busy}
                            onClick={() => act('revert', () => ccmApi.ne2StyleRevert(h.version))}>Restore</button>
                  )}
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
